import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { CoreApplicationContext } from '@eventbrite/legacy-infra.context-types';
import { ConsumerHeaderSearchBar } from '@eventbrite/consumer-header.global-nav';
import { getLogoInfo, getUserMenuInfo } from '@eventbrite/legacy-infra.site-header';
import classNames from 'classnames';
import { IconButton, MagnifyingGlassIcon, ChevronLeftIcon } from '@eventbrite/marmalade';
import type {
    FeatureFlags,
    RequestShape,
    searchBarProps,
    UserShape,
} from '@eventbrite/consumer-header.global-nav/types';
import { GlobalHeaderLogo } from '../GlobalHeaderLogo';
import { GlobalHeaderMenus } from '../GlobalHeaderNavigationBar';
import {
    ConsumerHeaderDesktopLinks,
    ConsumerHeaderMobileLinks,
} from '../ConsumerHeaderLinks/ConsumerHeaderLinks';

import styles from './ConsumerHeader.module.scss';

const SMALL_WIDE_WIDTH_BREAKPOINT_PX = 660;

function useIsMobileSearchViewport(): boolean {
    const [isMobile, setIsMobile] = useState(true);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        const mql = window.matchMedia(`(max-width: ${SMALL_WIDE_WIDTH_BREAKPOINT_PX}px)`);
        const handler = () => setIsMobile(mql.matches);
        handler();
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return isMobile;
}

export interface ConsumerHeaderProps {
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: boolean;
        isManageOverviewActive?: boolean;
        localeInfo?: Partial<CoreApplicationContext['env']['localeInfo']>;
    };
    request: RequestShape;
    user: UserShape;
    onAdditionalMenuTextItemSelect?: Function;
    isHomePage?: boolean;
    id?: string;
    featureFlags: FeatureFlags;
    hasBreadcrumbs?: boolean;
    searchBar?: searchBarProps;
}
export const ConsumerHeader: React.FunctionComponent<ConsumerHeaderProps> = (props) => {
    const {
        user,
        env,
        id,
        featureFlags,
        hasBreadcrumbs,
        request,
        searchBar,
        isHomePage,
        onAdditionalMenuTextItemSelect,
    } = props;

    const { serverUrl, adminServerUrl, logoutUrl, helpUrls } = env;
    const { path: requestPath } = request;
    const serverUrlLanguageSubdirectoy = env?.localeInfo?.uses_language_subdirectory
        ? `${serverUrl}/${env.localeInfo.locale}`
        : `${serverUrl}`;
    const { formatMessage } = useIntl();

    const userMenuInfo = getUserMenuInfo({
        adminServerUrl,
        featureFlags: {
            ...featureFlags,
            isDetachInterestsEnabled: false,
        },
        logoutUrl,
        requestPath,
        serverUrl,
        showBrowseEvents: true,
        user,
        helpUrls,
        showHelpMenu: featureFlags?.showHelpCenterMenu ?? false,
    });

    const isMobileSearchViewport = useIsMobileSearchViewport();
    const [isSearchExpanded, setSearchExpanded] = useState(false);

    const isSingleRowHeader = isMobileSearchViewport;

    const headerClassnames = classNames(styles.root, styles.searchDropdown, {
        [styles.breadcrumbs as string]: hasBreadcrumbs,
        [styles.singleRow as string]: isSingleRowHeader,
    });

    const renderSearchArea = () => {
        if (!isMobileSearchViewport) {
            return <ConsumerHeaderSearchBar user={user} env={env} searchBar={searchBar} />;
        }
        if (isSearchExpanded) {
            return null;
        }
        return null;
    };

    const searchTriggerButton = (
        <IconButton
            icon={<MagnifyingGlassIcon size="small" />}
            aria-label={
                isSearchExpanded
                    ? formatMessage({
                          id: 'Close search',
                          defaultMessage: 'Close search',
                      })
                    : formatMessage({
                          id: 'Open search',
                          defaultMessage: 'Open search',
                      })
            }
            onClick={() => setSearchExpanded(!isSearchExpanded)}
            className={styles.searchTriggerButton}
            data-testid="consumer-header-search-trigger"
        />
    );

    return (
        <header
            id={id || 'global-header'}
            className={headerClassnames}
            data-testid="consumer-header"
        >
            <a className={styles.skipLinks} href="#skip-heading">
                {formatMessage({
                    id: 'Skip Main Navigation',
                    defaultMessage: 'Skip Main Navigation',
                })}
            </a>
            <div className={styles.headerContainer} data-role="header">
                <div className={styles.headerContent}>
                    <GlobalHeaderLogo
                        info={getLogoInfo(serverUrlLanguageSubdirectoy, requestPath, {
                            iconWidth: '110px',
                        })}
                    />
                    {renderSearchArea()}
                    <div
                        className={styles.desktopLinksContainer}
                        data-testid="consumer-header-desktop"
                    >
                        <ConsumerHeaderDesktopLinks
                            user={user}
                            env={env}
                            featureFlags={featureFlags}
                            requestPath={requestPath}
                            isHomePage={isHomePage}
                            onAdditionalMenuTextItemSelect={onAdditionalMenuTextItemSelect}
                        />
                        <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                    </div>
                    {isMobileSearchViewport ? (
                        <div className={styles.rightGroup}>
                            {searchTriggerButton}
                            <div
                                className={styles.mobileLinksContainer}
                                data-testid="consumer-header-mobile"
                            >
                                <ConsumerHeaderMobileLinks
                                    user={user}
                                    env={env}
                                    isHomePage={isHomePage}
                                    featureFlags={featureFlags}
                                    requestPath={requestPath}
                                />
                            </div>
                        </div>
                    ) : (
                        <div
                            className={styles.mobileLinksContainer}
                            data-testid="consumer-header-mobile"
                        >
                            <ConsumerHeaderMobileLinks
                                user={user}
                                env={env}
                                isHomePage={isHomePage}
                                featureFlags={featureFlags}
                                requestPath={requestPath}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.headerContent}>
                    {isMobileSearchViewport && isSearchExpanded && (
                        <div
                            className={styles.searchExpandedBelow}
                            data-testid="consumer-header-search-expanded"
                        >
                            <ConsumerHeaderSearchBar user={user} env={env} searchBar={searchBar} />
                        </div>
                    )}
                </div>
            </div>
            <span id="skip-heading" className={styles.skipHeading}>
                {formatMessage({ id: 'Page Content', defaultMessage: 'Page Content' })}
            </span>
        </header>
    );
};
